import DOMPurify, { type Config } from "dompurify";

const FORBIDDEN_TAGS = [
    "script",
    "iframe",
    "object",
    "embed",
    "form",
    "input",
    "button",
    "textarea",
    "select",
    "style",
    "link",
    "meta",
    "base",
    "svg",
    "math",
    "video",
    "audio",
    "applet",
    "frame",
    "frameset",
    "noscript",
];

const SANITIZE_CONFIG: Config = {
    USE_PROFILES: { html: true },
    FORBID_TAGS: FORBIDDEN_TAGS,
    FORBID_ATTR: [
        "onerror",
        "onload",
        "onclick",
        "onmouseover",
        "onfocus",
        "onblur",
        "onchange",
        "onsubmit",
        "onpointerdown",
        "srcdoc",
    ],
    ADD_TAGS: ["font"],
    ADD_ATTR: ["target", "rel", "align", "face", "size", "color"],
    ALLOW_DATA_ATTR: false,
    ADD_DATA_URI_TAGS: ["img"],
};

const INJECTED_CODE_PATTERNS: RegExp[] = [
    /<\?(?:php|=)[\s\S]*?\?>/gi,
    /<\?(?:php|=)/gi,
    /&lt;\?(?:php|=)[\s\S]*?\?(?:&gt;|>)/gi,
    /&lt;\?(?:php|=)/gi,
    /&#0*60;\?(?:php|=)[\s\S]*?\?(?:&#0*62;|>)/gi,
    /&#0*60;\?(?:php|=)/gi,
    /&#x0*3c;\?(?:php|=)[\s\S]*?\?(?:&#x0*3e;|>)/gi,
    /&#x0*3c;\?(?:php|=)/gi,
    /<%[\s\S]*?%>/gi,
    /<%/g,
    /&lt;%[\s\S]*?%(?:&gt;|>)/gi,
    /&lt;%/gi,
    /<script\b[\s\S]*?<\/script>/gi,
    /&lt;script\b[\s\S]*?&lt;\/script&gt;/gi,
];

const INJECTED_TEXT_PATTERNS: RegExp[] = [
    /<\?(?:php|=)[\s\S]*?\?>/gi,
    /<\?(?:php|=)/gi,
    /<%[\s\S]*?%>/gi,
    /<%/g,
    /<script\b[\s\S]*?<\/script>/gi,
    /javascript\s*:/gi,
    /vbscript\s*:/gi,
];

let hooksRegistered = false;

const applyPatterns = (value: string, patterns: RegExp[]): string => {
    return patterns.reduce(
        (acc, pattern) => acc.replace(new RegExp(pattern.source, pattern.flags), ""),
        value
    );
};

const stripInjectedCode = (html: string): string => {
    return applyPatterns(html, INJECTED_CODE_PATTERNS);
};

const stripInjectedCodeFromTextNodes = (html: string): string => {
    if (!html || typeof document === "undefined") {
        return html;
    }

    const template = document.createElement("template");
    template.innerHTML = html;

    const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];

    while (walker.nextNode()) {
        textNodes.push(walker.currentNode as Text);
    }

    textNodes.forEach((node) => {
        node.textContent = applyPatterns(node.textContent ?? "", INJECTED_TEXT_PATTERNS);
    });

    return template.innerHTML;
};

const registerSanitizerHooks = (): void => {
    if (hooksRegistered || typeof window === "undefined") {
        return;
    }

    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
        if (!(node instanceof Element)) {
            return;
        }

        if (node.tagName === "A") {
            const href = node.getAttribute("href") ?? "";

            if (/^\s*(javascript|vbscript|data):/i.test(href)) {
                node.removeAttribute("href");
            }

            if (node.getAttribute("target") === "_blank") {
                node.setAttribute("rel", "noopener noreferrer");
            }
        }

        if (node.tagName === "IMG") {
            const src = node.getAttribute("src") ?? "";

            if (src && !/^(https?:|data:image\/)/i.test(src)) {
                node.removeAttribute("src");
            }
        }
    });

    hooksRegistered = true;
};

const textNodesContainInjectedCode = (html: string): boolean => {
    if (!html || typeof document === "undefined") {
        return false;
    }

    const template = document.createElement("template");
    template.innerHTML = html;
    const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);

    while (walker.nextNode()) {
        const text = walker.currentNode.textContent ?? "";
        if (applyPatterns(text, INJECTED_TEXT_PATTERNS) !== text) {
            return true;
        }
    }

    return false;
};

export const hasInjectedCode = (html: string): boolean => {
    if (!html) {
        return false;
    }

    return stripInjectedCode(html) !== html || textNodesContainInjectedCode(html);
};

export const sanitizeRichText = (html: string): string => {
    if (!html) {
        return "";
    }

    registerSanitizerHooks();

    const withoutInjectedCode = stripInjectedCodeFromTextNodes(
        stripInjectedCode(html)
    );

    return String(DOMPurify.sanitize(withoutInjectedCode, SANITIZE_CONFIG));
};

// Rich text editors can return markup such as "<p><br></p>" when they look empty.
// This checks whether the HTML holds any visible text.
export const hasHtmlText = (html: string | null | undefined): boolean =>
    Boolean(
        (html ?? "")
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/gi, " ")
            .trim()
    );

export const normalizeRichTextValue = (html: string): string => {
    const sanitized = sanitizeRichText(html);

    return hasHtmlText(sanitized) ? sanitized : "";
};
